import { h, type FunctionalComponent, type SVGAttributes } from 'vue'

type IconElement = 'path' | 'rect' | 'circle' | 'ellipse' | 'line' | 'polyline'
type IconNode = readonly [IconElement, Readonly<Record<string, string>>]

// The shell draws icons at 14–18px. These are drawn on a 16px grid with a
// 1.5px stroke so they stay crisp there; Heroicons' 24px outlines thin to a
// ~1px stroke at that size. Straight strokes sit on .25/.75 coordinates.
function defineIcon(nodes: readonly IconNode[]): FunctionalComponent<SVGAttributes> {
  return () =>
    h(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 16 16',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '1.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'aria-hidden': 'true',
        'data-slot': 'icon'
      },
      nodes.map(([tag, attrs]) => h(tag, attrs))
    )
}

export const shellIcons = {
  home: defineIcon([
    ['path', { d: 'M1.25 7.25 8 1.75l6.75 5.5M2.75 6.25v6.5a1 1 0 0 0 1 1h2.5V9.25h3.5v4.5h2.5a1 1 0 0 0 1-1v-6.5' }]
  ]),
  people: defineIcon([
    ['circle', { cx: '5.5', cy: '4.5', r: '2.25' }],
    ['path', { d: 'M1.25 14.25v-1.5a4.25 3.5 0 0 1 8.5 0v1.5M11.75 4.75h3M11.75 8.75h3M12.75 12.75h2' }]
  ]),
  segments: defineIcon([
    ['path', { d: 'M6.75 1.75a6.25 6.25 0 1 0 7.5 7.5h-7.5ZM9.75 1.75v4.5h4.5a6.25 6.25 0 0 0-4.5-4.5Z' }]
  ]),
  responses: defineIcon([
    ['path', { d: 'M4.25 1.75h7.5M2.75 4.25h10.5' }],
    ['path', { d: 'M1.75 9.25h3.25l1 1.5h4l1-1.5h3.25' }],
    ['path', { d: 'M1.75 9.25v3.5a1 1 0 0 0 1 1h10.5a1 1 0 0 0 1-1v-3.5l-1.25-2.5h-10Z' }]
  ]),
  questions: defineIcon([
    ['path', { d: 'M4.25 12.75 1.25 14.75V3.25a2 2 0 0 1 2-2h9.5a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2Z' }],
    ['path', { d: 'M6.5 5.25a1.5 1.5 0 1 1 2.6 1.02c-.6.4-1.1.73-1.1 1.48' }],
    ['circle', { cx: '8', cy: '10.25', r: '0.85', fill: 'currentColor', stroke: 'none' }]
  ]),
  flows: defineIcon([
    ['rect', { x: '5.25', y: '1.25', width: '5.5', height: '4', rx: '1.25' }],
    ['path', { d: 'M8.25 5.25v3M3.75 10.75v-2.5h8.5v2.5' }],
    ['rect', { x: '1.25', y: '10.75', width: '5', height: '4', rx: '1.25' }],
    ['rect', { x: '9.75', y: '10.75', width: '5', height: '4', rx: '1.25' }]
  ]),
  widgets: defineIcon([
    ['path', { d: 'M3.25 11.25h-.5a1.5 1.5 0 0 1-1.5-1.5v-7a1.5 1.5 0 0 1 1.5-1.5h8a1.5 1.5 0 0 1 1.5 1.5v.5' }],
    ['rect', { x: '5.25', y: '5.75', width: '9.5', height: '8', rx: '1.5' }],
    ['path', { d: 'M8.25 9.75h3.5' }]
  ]),
  pages: defineIcon([
    ['rect', { x: '1.25', y: '1.75', width: '13.5', height: '12.5', rx: '1.5' }],
    ['path', { d: 'M1.25 4.75h13.5M4.25 8.25h7.5M4.25 11.25h4.5' }]
  ]),
  forms: defineIcon([
    ['rect', { x: '1.75', y: '1.75', width: '12.5', height: '3.5', rx: '1.5' }],
    ['rect', { x: '1.75', y: '7.75', width: '12.5', height: '3.5', rx: '1.5' }],
    ['rect', { x: '1.75', y: '13.75', width: '5', height: '1.5', rx: '0.75', fill: 'currentColor', stroke: 'none' }]
  ]),
  offers: defineIcon([
    [
      'path',
      {
        d: 'M3.75 3.75h-1a1.5 1.5 0 0 0-1.5 1.5v5.5a1.5 1.5 0 0 0 1.5 1.5h1M6.25 3.75h3M7.75 3.75v8.5M10.5 12.25l2.25-8.5 2.25 8.5M11.1 10h3.3'
      }
    ]
  ]),
  emails: defineIcon([
    ['rect', { x: '1.25', y: '2.75', width: '13.5', height: '10.5', rx: '1.5' }],
    ['path', { d: 'm1.75 3.75 6.25 5 6.25-5' }]
  ]),
  campaigns: defineIcon([
    ['path', { d: 'M6.75 14.25h-3a2 2 0 0 1-2-2v-8.5a2 2 0 0 1 2-2h8.5a2 2 0 0 1 2 2v3M1.75 5.25h12.5' }],
    ['circle', { cx: '11.75', cy: '9.25', r: '1.25' }],
    ['path', { d: 'M8.75 14.75a3 2 0 0 1 6 0' }]
  ]),
  goals: defineIcon([
    ['circle', { cx: '8', cy: '8', r: '6.5' }],
    ['circle', { cx: '8', cy: '8', r: '3.5' }],
    ['circle', { cx: '8', cy: '8', r: '0.75', fill: 'currentColor', stroke: 'none' }]
  ]),
  ideas: defineIcon([['path', { d: 'M5.25 11.25V10a5 5 0 1 1 5.5 0v1.25h-5.5ZM5.75 14.25h4.5' }]]),
  ads: defineIcon([
    ['path', { d: 'M1.75 6.25v3.5h2.5l6.5 3.5V2.75l-6.5 3.5Z' }],
    ['path', { d: 'm4.75 9.75.75 4h1.75M13.25 6.25v3.5' }]
  ]),
  optimizations: defineIcon([['path', { d: 'm1.75 12.75 4.5-4.5 3 2.5 5-8M9.25 2.75h5v5' }]]),
  banknotes: defineIcon([
    ['rect', { x: '1.25', y: '2.75', width: '13.5', height: '8.5', rx: '1.5' }],
    ['circle', { cx: '8', cy: '7', r: '2' }],
    ['path', { d: 'M4.25 7h.01M11.75 7h.01M2.75 13.75h10.5' }]
  ]),
  abTesting: defineIcon([
    [
      'path',
      {
        d: 'M5.25 1.75h5.5M6.25 1.75v4.5L2.5 11.7a1.6 1.6 0 0 0 1.3 2.55h8.4a1.6 1.6 0 0 0 1.3-2.55L9.75 6.25v-4.5M4.75 9.25h6.5'
      }
    ]
  ]),
  settings: defineIcon([
    [
      'path',
      {
        d: 'm6.25 1.25-.5 2-1.5.75-2-.5-1.5 2.5 1.5 1.5v1l-1.5 1.5 1.5 2.5 2-.5 1.5.75.5 2h3.5l.5-2 1.5-.75 2 .5 1.5-2.5-1.5-1.5v-1l1.5-1.5-1.5-2.5-2 .5-1.5-.75-.5-2Z'
      }
    ],
    ['circle', { cx: '8', cy: '8', r: '2.25' }]
  ]),
  moon: defineIcon([['path', { d: 'M13.75 10A6.25 6.25 0 0 1 6 1.75 6.25 6.25 0 1 0 13.75 10Z' }]]),
  sun: defineIcon([
    ['circle', { cx: '8', cy: '8', r: '3' }],
    [
      'path',
      {
        d: 'M8.25 1.25v1M8.25 13.75v1M1.25 8.25h1M13.75 8.25h1M3.25 3.25l.5.5M12.25 12.25l.5.5M3.25 12.75l.5-.5M12.25 3.75l.5-.5'
      }
    ]
  ]),
  sidebarCollapse: defineIcon([
    ['rect', { x: '1.25', y: '2.25', width: '13.5', height: '11.5', rx: '1.5' }],
    ['path', { d: 'M5.25 2.25v11.5m6.5-8-2.5 2.5 2.5 2.5' }]
  ]),
  sidebarExpand: defineIcon([
    ['rect', { x: '1.25', y: '2.25', width: '13.5', height: '11.5', rx: '1.5' }],
    ['path', { d: 'M5.25 2.25v11.5m4  -8 2.5 2.5-2.5 2.5' }]
  ]),
  search: defineIcon([
    ['circle', { cx: '6.5', cy: '6.5', r: '4.75' }],
    ['path', { d: 'm10.25 10.25 4 4' }]
  ]),
  chevronUpDown: defineIcon([['path', { d: 'm4.75 5.75 3.5-3.5 3.5 3.5m-7 4.5 3.5 3.5 3.5-3.5' }]]),
  chevronDown: defineIcon([['path', { d: 'm3.75 5.75 4.5 4.5 4.5-4.5' }]]),
  arrowLeft: defineIcon([['path', { d: 'M14.25 8.25H1.75m5-5-5 5 5 5' }]]),
  arrowRight: defineIcon([['path', { d: 'M1.75 8.25h12.5m-5-5 5 5-5 5' }]]),
  menu: defineIcon([['path', { d: 'M2.25 3.75h11.5M2.25 8.25h11.5M2.25 12.75h11.5' }]]),
  folder: defineIcon([
    [
      'path',
      {
        d: 'M1.25 4.25a1.5 1.5 0 0 1 1.5-1.5h3l2 2h5.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H2.75a1.5 1.5 0 0 1-1.5-1.5Z'
      }
    ]
  ]),
  sparkles: defineIcon([
    [
      'path',
      {
        d: 'm6 5 1.25 3.5 3.5 1.25-3.5 1.25L6 14.5 4.75 11l-3.5-1.25 3.5-1.25Zm6-3.75.75 2.25L15 4.25 12.75 5 12 7.25 11.25 5 9 4.25l2.25-.75Z'
      }
    ]
  ]),
  key: defineIcon([
    ['circle', { cx: '5', cy: '5', r: '3.25' }],
    ['path', { d: 'm7.25 7.25 7 7m-4.5-4.5 2-2m.5 4.5 2-2' }]
  ]),
  building: defineIcon([
    ['rect', { x: '2.75', y: '1.75', width: '10.5', height: '12.5', rx: '1.5' }],
    ['path', { d: 'M6.25 14.25v-3.5h3.5v3.5M6.25 4.75h3.5M6.25 7.75h3.5' }]
  ]),
  clients: defineIcon([
    ['circle', { cx: '4.5', cy: '4.5', r: '2' }],
    ['circle', { cx: '11.5', cy: '4.5', r: '2' }],
    ['path', { d: 'M1.25 13.25v-1.5a3.25 3.25 0 0 1 6.5 0v1.5M9.75 8.75a3.25 3.25 0 0 1 5 3v1.5' }]
  ]),
  creditCard: defineIcon([
    ['rect', { x: '1.25', y: '2.75', width: '13.5', height: '10.5', rx: '1.5' }],
    ['path', { d: 'M1.25 6.25h13.5M4.25 10.25h2.5' }]
  ]),
  signOut: defineIcon([
    [
      'path',
      { d: 'M6.25 1.75h-3a1.5 1.5 0 0 0-1.5 1.5v9.5a1.5 1.5 0 0 0 1.5 1.5h3M6.25 8.25h8m-3.5-3.5 3.5 3.5-3.5 3.5' }
    ]
  ]),
  attribution: defineIcon([
    ['circle', { cx: '3.5', cy: '3.5', r: '2.25' }],
    ['path', { d: 'M3.75 5.75v3a3 3 0 0 0 3 3h7.5m-3-3 3 3-3 3' }]
  ]),
  advanced: defineIcon([
    ['path', { d: 'M1.75 4.75h3m4 0h5.5M1.75 11.25h6m4 0h2.5' }],
    ['circle', { cx: '7', cy: '4.75', r: '2' }],
    ['circle', { cx: '10', cy: '11.25', r: '2' }]
  ]),
  collaborators: defineIcon([
    ['circle', { cx: '8', cy: '4.25', r: '2.25' }],
    ['circle', { cx: '3.5', cy: '6', r: '1.75' }],
    ['circle', { cx: '12.5', cy: '6', r: '1.75' }],
    [
      'path',
      {
        d: 'M4.25 14.25v-1a3.75 3.75 0 0 1 7.5 0v1M1.25 12.75v-.75a2.5 2.5 0 0 1 3.4-2.33M14.75 12.75v-.75a2.5 2.5 0 0 0-3.4-2.33'
      }
    ]
  ]),
  codeSquare: defineIcon([
    ['rect', { x: '1.75', y: '1.75', width: '12.5', height: '12.5', rx: '2.25' }],
    ['path', { d: 'm6.25 5.75-2.25 2.5 2.25 2.5m3.5-5 2.25 2.5-2.25 2.5' }]
  ]),
  squaresPlus: defineIcon([
    ['rect', { x: '1.75', y: '1.75', width: '5', height: '5', rx: '1.25' }],
    ['rect', { x: '9.25', y: '1.75', width: '5', height: '5', rx: '1.25' }],
    ['rect', { x: '1.75', y: '9.25', width: '5', height: '5', rx: '1.25' }],
    ['path', { d: 'M11.75 9.25v5M9.25 11.75h5' }]
  ]),
  rectangleGroup: defineIcon([
    ['rect', { x: '1.25', y: '2.25', width: '13.5', height: '11.5', rx: '1.75' }],
    ['rect', { x: '3.75', y: '4.75', width: '3.5', height: '6.5', rx: '0.75' }],
    ['path', { d: 'M9.25 5.25h3M9.25 8.25h3M9.25 11.25h3' }]
  ]),
  shieldCheck: defineIcon([
    ['path', { d: 'M8 1.25 2.25 3.5v4.25c0 3.25 2.4 5.6 5.75 7 3.35-1.4 5.75-3.75 5.75-7V3.5Z' }],
    ['path', { d: 'm5.5 8.25 1.75 1.75 3.25-3.5' }]
  ]),
  userCircle: defineIcon([
    ['circle', { cx: '8', cy: '8', r: '6.75' }],
    ['circle', { cx: '8', cy: '6.5', r: '2.25' }],
    ['path', { d: 'M3.75 12.75a5 3.25 0 0 1 8.5 0' }]
  ]),
  lockClosed: defineIcon([
    ['rect', { x: '2.75', y: '7.25', width: '10.5', height: '7.5', rx: '1.5' }],
    ['path', { d: 'M5.25 7.25V5a2.75 2.75 0 0 1 5.5 0v2.25' }]
  ]),
  bell: defineIcon([
    [
      'path',
      {
        d: 'M8 1.75A3.25 3.25 0 0 0 4.75 5c0 4-1.5 6.25-1.5 6.25h9.5s-1.5-2.25-1.5-6.25A3.25 3.25 0 0 0 8 1.75ZM6.5 13.75a1.6 1.6 0 0 0 3 0'
      }
    ]
  ]),
  gift: defineIcon([
    ['rect', { x: '1.75', y: '5.25', width: '12.5', height: '3', rx: '0.75' }],
    [
      'path',
      {
        d: 'M2.75 8.25v5a1.5 1.5 0 0 0 1.5 1.5h7.5a1.5 1.5 0 0 0 1.5-1.5v-5M8 5.25v9.5M8 5.25s-.75-2.6-1.75-3.1c-1-.5-2 .25-2 1.25 0 1.5 2.25 1.85 3.75 1.85Zm0 0s.75-2.6 1.75-3.1c1-.5 2 .25 2 1.25 0 1.5-2.25 1.85-3.75 1.85Z'
      }
    ]
  ])
} as const

export type ShellIconName = keyof typeof shellIcons
